export type Preference = { key: string; values: string[] }
export type Prefs = Preference[] | undefined

/**
 * Generic helper to upsert an item in a keyed collection (e.g. preferences or metadata).
 * Preserves all existing items except those with the same key as the new item.
 */
export const upsertByKey = <T extends { key: string }>(
  items: T[] | undefined,
  next: T,
): T[] => {
  return [...(items ?? []).filter((item) => item.key !== next.key), next]
}

/**
 * Lookup for a preference value
 * @param prefs - The preferences array
 * @param key - The key to lookup
 * @param fallback - The fallback value if the key is not found
 * @returns The value of the key or the fallback value
 */
export const pref = (
  prefs: Prefs,
  key: string,
  fallback?: string,
): string | undefined => {
  return prefs?.find((p) => p.key === key)?.values?.[0] ?? fallback
}

/**
 * Boolean variant for a preference value
 * @param prefs - The preferences array
 * @param key - The key to lookup
 * @param fallback - The fallback value if the key is not found
 * @returns The boolean value of the key or the fallback value
 */
export const prefBool = (
  prefs: Prefs,
  key: string,
  fallback = false,
): boolean => {
  const raw = pref(prefs, key, fallback ? 'true' : 'false')
  return raw !== 'false'
}

/**
 * Upsert / overwrite a preference value
 * @param prefs - The preferences array
 * @param key - The key to upsert
 * @param values - The values to upsert
 * @returns The updated preferences array
 */
export const setPref = (
  prefs: Prefs,
  key: string,
  values: string | string[],
): Preference[] => {
  const arr = Array.isArray(values) ? values : [values]
  return upsertByKey(prefs, { key, values: arr })
}
